import type { Car } from '@multirx/shared/types/car.js';
import {
  carPower,
  clampCarStat,
  effectiveStart,
  effectiveStraightLine,
} from '@multirx/shared/types/car.js';
import type { Driver } from '@multirx/shared/types/driver.js';
import {
  driverPower,
  effectiveCombativeness,
  effectiveCorner,
} from '@multirx/shared/types/driver.js';
import type { AuthUser } from '@multirx/shared/types/user.js';
import { formatDriverTitleChips } from '@multirx/shared/game/driverPalmares.js';
import type { DriverSeasonProfile } from '@multirx/shared/game/driverPalmares.js';
import {
  NATIONALITIES,
  getFlagEmoji,
  getNationalityLabel,
  normalizeCountryCode,
} from '@multirx/shared/types/nationalities.js';
import { clampChampionshipStarLevel } from '@multirx/shared/game/starLadder.js';
import { nationalityFlagMarkup } from '../components/nationalityFlag.js';
import {
  mountInteractiveStarsCar,
  mountInteractiveStarsDriver,
  renderStarsCar,
  renderStarsDriver,
} from '../components/starRating.js';
import {
  assignCarImageFromLibrary,
  assignCarModel3dPath,
  fetchCarById,
  updateCar,
  uploadCarImage,
  uploadCarModel3d,
} from '../cars/api.js';
import { mountCar3dPreview, type Car3dPreviewHandle } from '../cars/car3dPreview.js';
import { resolveCar3dModelUrl } from '../cars/car3dModelMap.js';
import { cacheBustCarGlbUrl } from '../cars/carOrientationFix.js';
import {
  assignDriverImageFromLibrary,
  fetchDriverById,
  updateDriver,
  uploadDriverImage,
} from '../drivers/api.js';
import { fetchActiveGlobalSeason, fetchDriverSeasonProfile } from '../championship/api.js';
import { mountEntityImageBlock } from '../ui/entityImageBlock.js';
import { openCar3dModelPicker } from '../ui/car3dModelPicker.js';
import {
  mountGarageEntityRename,
  normalizeGarageDriverNamePart,
  normalizeGarageDriverNationality,
  MAX_GARAGE_DRIVER_NAME_PART_LENGTH,
} from '../garage/garageEntityRename.js';
import { getCurrentUser } from './session.js';
import { applyRoleVisibility } from './navAccount.js';
import { canEditDriverStats, divisionLabel, isPalmaresEmpty } from './accountProfileHelpers.js';
import {
  mountAccountProfileResultsTable,
  type AccountProfileResultsHandle,
} from './accountProfileResults.js';

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

function assetFileLabel(path: string | null | undefined, empty = 'Aucun fichier'): string {
  if (path == null || path.trim() === '') return empty;
  const clean = path.replace(/\\/g, '/').split('?')[0] ?? path;
  const base = clean.split('/').filter(Boolean).pop();
  return base && base.length > 0 ? base : empty;
}

function createStaffAssetBadge(label: string, path: string | null | undefined): HTMLElement {
  const badge = document.createElement('span');
  badge.className = 'account-asset-badge account-asset-badge--overlay';
  badge.dataset.role = 'admin,moderator';
  badge.title = `${label} : ${assetFileLabel(path)}`;
  badge.innerHTML = `<span class="account-asset-badge__label">${label}</span><code class="account-asset-badge__file">${assetFileLabel(path)}</code>`;
  return badge;
}

function setStaffAssetBadgeFile(el: HTMLElement, path: string | null | undefined): void {
  const file = assetFileLabel(path);
  const code = el.querySelector('.account-asset-badge__file');
  const label = el.querySelector('.account-asset-badge__label')?.textContent ?? 'Fichier';
  if (code) code.textContent = file;
  el.title = `${label} : ${file}`;
}

/** Badge staff en bas à droite d’un cadre image / 3D. */
function pinStaffBadgeOnMedia(frame: HTMLElement, badge: HTMLElement): void {
  frame.classList.add('account-profile-media-frame');
  if (!badge.isConnected || badge.parentElement !== frame) {
    frame.appendChild(badge);
  }
}

function buildDriverNationalityOptions(
  currentCode: string
): Array<{ value: string; label: string }> {
  const options = NATIONALITIES.map((entry) => ({
    value: entry.code,
    label: `${entry.flagEmoji} ${entry.label}`,
  }));
  const current = normalizeCountryCode(currentCode);
  if (current && !options.some((option) => option.value === current)) {
    options.unshift({
      value: current,
      label: `${getFlagEmoji(current)} ${getNationalityLabel(current)}`,
    });
  }
  return options;
}

type ProfileTab = 'pilot' | 'results' | 'palmares';

/** Monte le profil compte : onglets Pilote | Résultats | Palmarès. */
export function mountAccountProfileView(body: HTMLElement, user: AuthUser): void {
  body.innerHTML = '';

  const shell = document.createElement('section');
  shell.className = 'account-profile-activity';

  const tabsNav = document.createElement('nav');
  tabsNav.className = 'garage-entity-tabs account-profile-tabs';
  tabsNav.setAttribute('role', 'tablist');
  tabsNav.setAttribute('aria-label', 'Profil du compte');

  const panel = document.createElement('div');
  panel.className = 'account-profile-activity__panel';

  const pilotPanel = document.createElement('div');
  pilotPanel.className = 'account-profile-activity__pilot';
  pilotPanel.setAttribute('role', 'tabpanel');

  const resultsHost = document.createElement('div');
  resultsHost.className = 'account-profile-activity__results';
  resultsHost.setAttribute('role', 'tabpanel');
  resultsHost.hidden = true;

  const palmaresHost = document.createElement('div');
  palmaresHost.className = 'account-profile-activity__palmares';
  palmaresHost.setAttribute('role', 'tabpanel');
  palmaresHost.hidden = true;

  const grid = document.createElement('div');
  grid.className = 'account-profile-grid';

  const pilotCol = document.createElement('div');
  pilotCol.className = 'account-profile-col account-profile-col--pilot';
  const carCol = document.createElement('div');
  carCol.className = 'account-profile-col account-profile-col--car';

  const driverImageBadge = createStaffAssetBadge('Image', null);
  const carImageBadge = createStaffAssetBadge('Image', null);
  const carModelBadge = createStaffAssetBadge('Modèle 3D', null);

  grid.append(pilotCol, carCol);
  pilotPanel.appendChild(grid);
  panel.append(pilotPanel, resultsHost, palmaresHost);

  const tabButtons = new Map<ProfileTab, HTMLButtonElement>();

  const addTab = (id: ProfileTab, label: string): HTMLButtonElement => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'garage-entity-tab';
    btn.textContent = label;
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-selected', 'false');
    btn.dataset.tab = id;
    tabsNav.appendChild(btn);
    tabButtons.set(id, btn);
    return btn;
  };

  addTab('pilot', 'Pilote');
  addTab('results', 'Résultats');

  let resultsHandle: AccountProfileResultsHandle | null = null;
  let active: ProfileTab = 'pilot';

  const setTab = (tab: ProfileTab): void => {
    if (tab === 'palmares' && !tabButtons.has('palmares')) return;
    active = tab;
    for (const [id, btn] of tabButtons) {
      const on = id === tab;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-selected', on ? 'true' : 'false');
    }
    pilotPanel.hidden = tab !== 'pilot';
    resultsHost.hidden = tab !== 'results';
    palmaresHost.hidden = tab !== 'palmares';
  };

  for (const [id, btn] of tabButtons) {
    btn.addEventListener('click', () => setTab(id));
  }

  shell.append(tabsNav, panel);
  body.appendChild(shell);
  setTab('pilot');

  const ensurePalmaresTab = (profile: DriverSeasonProfile | null): void => {
    const show = !isPalmaresEmpty(profile);
    const existing = tabButtons.get('palmares');
    if (!show) {
      if (existing) {
        existing.remove();
        tabButtons.delete('palmares');
        if (active === 'palmares') setTab('pilot');
      }
      palmaresHost.innerHTML = '';
      return;
    }
    if (!existing) {
      const btn = addTab('palmares', 'Palmarès');
      btn.addEventListener('click', () => setTab('palmares'));
    }
    if (profile) renderPalmaresPanel(palmaresHost, profile);
  };

  if (user.driverId == null) {
    const empty = document.createElement('p');
    empty.className = 'muted';
    empty.textContent = 'Aucun pilote lié à ce compte.';
    pilotCol.appendChild(empty);
    resultsHost.innerHTML = '<p class="muted">Résultats indisponibles sans pilote.</p>';
  } else {
    void fillPilotColumn(pilotCol, user.driverId, driverImageBadge, {
      onDriverSaved: (saved) => resultsHandle?.setDriver(saved),
      onProfileReady: (driver, profile) => {
        resultsHandle?.destroy();
        resultsHost.innerHTML = '';
        resultsHandle = mountAccountProfileResultsTable(resultsHost, driver);
        ensurePalmaresTab(profile);
        setTab(active);
      },
    });
  }

  if (user.carId == null) {
    const empty = document.createElement('p');
    empty.className = 'muted';
    empty.textContent = 'Aucune voiture liée à ce compte.';
    carCol.appendChild(empty);
  } else {
    void fillCarColumn(carCol, user.carId, carImageBadge, carModelBadge);
  }

  applyRoleVisibility();
}

function renderPalmaresPanel(host: HTMLElement, profile: DriverSeasonProfile): void {
  const titles = formatDriverTitleChips(profile.titles);
  const s = profile.season;
  host.innerHTML = `
    <ul class="account-profile-palmares__stats">
      <li><span>Classement</span><strong>${s.rank > 0 ? `#${s.rank}` : '—'}</strong></li>
      <li><span>Points</span><strong>${s.championshipPoints}</strong></li>
      <li><span>Finales gagnées</span><strong>${s.finalsWon}</strong></li>
      <li><span>Podiums</span><strong>${s.podiums}</strong></li>
    </ul>
    ${
      titles.length
        ? `<div class="account-profile-titles">${titles
            .map((t) => `<span class="account-profile-title-chip">${escapeHtml(t)}</span>`)
            .join('')}</div>`
        : '<p class="muted">Aucun titre cette saison.</p>'
    }
  `;
}

type PilotFillOptions = {
  onDriverSaved: (driver: Driver) => void;
  onProfileReady: (driver: Driver, profile: DriverSeasonProfile | null) => void;
};

async function fillPilotColumn(
  col: HTMLElement,
  driverId: number,
  imageBadge: HTMLElement,
  opts: PilotFillOptions
): Promise<void> {
  const header = document.createElement('div');
  header.className = 'account-profile-header';
  const identity = document.createElement('div');
  identity.className = 'account-profile-identity';
  const statsHost = document.createElement('div');
  statsHost.className = 'account-profile-stats';
  header.append(identity, statsHost);
  col.appendChild(header);

  const imageWrap = document.createElement('div');
  col.appendChild(imageWrap);

  let driver: Driver;
  try {
    driver = await fetchDriverById(driverId);
  } catch {
    identity.innerHTML = '<p class="muted">Impossible de charger le pilote.</p>';
    return;
  }

  let current = driver;

  const paintIdentity = (): void => {
    const flag = nationalityFlagMarkup(current.nationality);
    identity.innerHTML = `
      <div class="circuits-detail-title-row account-profile-name-row">
        <p class="account-profile-name">${escapeHtml(`${current.firstName} ${current.lastName}`.trim())} ${flag}</p>
        <button type="button" class="circuits-detail-rename-trigger" aria-label="Modifier le pilote" title="Modifier">✎</button>
      </div>
      <p class="circuits-detail-rename-status" role="status" hidden></p>
      <p class="account-profile-meta">Division ${escapeHtml(divisionLabel(current.division))}</p>
    `;
    const titleRow = identity.querySelector('.circuits-detail-title-row') as HTMLElement;
    mountGarageEntityRename({
      section: identity,
      titleRow,
      fields: [
        {
          id: 'firstName',
          label: 'Prénom',
          value: current.firstName,
          maxLength: MAX_GARAGE_DRIVER_NAME_PART_LENGTH,
          placeholder: 'Prénom',
        },
        {
          id: 'lastName',
          label: 'Nom',
          value: current.lastName,
          maxLength: MAX_GARAGE_DRIVER_NAME_PART_LENGTH,
          placeholder: 'Nom',
        },
        {
          kind: 'select',
          id: 'nationality',
          label: 'Nationalité',
          value: current.nationality,
          options: buildDriverNationalityOptions(current.nationality),
        },
      ],
      onSave: async (values) => {
        const firstName = normalizeGarageDriverNamePart(values.firstName ?? '');
        const lastName = normalizeGarageDriverNamePart(values.lastName ?? '');
        const nationality = normalizeGarageDriverNationality(values.nationality ?? '');
        if (!firstName || !lastName) {
          throw new Error(
            `Prénom et nom requis (1–${MAX_GARAGE_DRIVER_NAME_PART_LENGTH} caractères chacun).`
          );
        }
        if (!nationality) throw new Error('Sélectionnez une nationalité valide.');
        if (current.id == null) return;
        const saved = await updateDriver(current.id, {
          firstName,
          lastName,
          nationality,
          pilotage: current.pilotage,
          defense: current.defense,
          imagePath: current.imagePath ?? null,
          division: current.division,
          defaultCarId: current.defaultCarId ?? null,
        });
        current = saved;
        paintIdentity();
        paintStats();
        opts.onDriverSaved(saved);
      },
      onRestore: () => paintIdentity(),
    });
  };

  const paintStats = (): void => {
    const cornerEff = effectiveCorner(current);
    const combatEff = effectiveCombativeness(current);
    const role = getCurrentUser()?.role;
    const editable = canEditDriverStats(role);

    statsHost.innerHTML = `
      <div class="account-profile-stat" data-stat="pilotage" data-current-value="${current.pilotage}">
        <span class="account-profile-stat__label">Pilotage</span>
        <span class="star-display"></span>
        <span class="account-profile-stat__eff">Bonus virage +${cornerEff}</span>
      </div>
      <div class="account-profile-stat" data-stat="defense" data-current-value="${current.defense}">
        <span class="account-profile-stat__label">Défense</span>
        <span class="star-display"></span>
        <span class="account-profile-stat__eff">Bonus combat +${combatEff}</span>
      </div>
      <div class="account-profile-stat account-profile-stat--total">
        <span class="account-profile-stat__label">Total</span>
        <strong data-driver-power>${driverPower(current)}★</strong>
      </div>
    `;

    const rows = statsHost.querySelectorAll<HTMLElement>('.account-profile-stat[data-stat]');
    rows.forEach((row) => {
      const display = row.querySelector('.star-display') as HTMLElement;
      const initial = clampChampionshipStarLevel(
        Number(row.dataset.currentValue ?? current[row.dataset.stat as 'pilotage' | 'defense'])
      );
      if (!display) return;
      if (!editable) {
        display.innerHTML = renderStarsDriver(initial);
        return;
      }
      mountInteractiveStarsDriver(display, initial, (value) => {
        row.dataset.currentValue = String(value);
        void saveStats();
      });
    });
  };

  const saveStats = async (): Promise<void> => {
    if (current.id == null || !canEditDriverStats(getCurrentUser()?.role)) return;
    const pilotageRow = statsHost.querySelector<HTMLElement>('[data-stat="pilotage"]');
    const defenseRow = statsHost.querySelector<HTMLElement>('[data-stat="defense"]');
    const pilotage = clampChampionshipStarLevel(
      Number(pilotageRow?.dataset.currentValue ?? current.pilotage)
    );
    const defense = clampChampionshipStarLevel(
      Number(defenseRow?.dataset.currentValue ?? current.defense)
    );
    statsHost.classList.add('account-profile-stats--saving');
    try {
      const saved = await updateDriver(current.id, {
        firstName: current.firstName,
        lastName: current.lastName,
        nationality: current.nationality,
        pilotage,
        defense,
        imagePath: current.imagePath ?? null,
        division: current.division,
        defaultCarId: current.defaultCarId ?? null,
      });
      current = saved;
      const power = statsHost.querySelector('[data-driver-power]');
      if (power) power.textContent = `${driverPower(saved)}★`;
      const cornerEff = effectiveCorner(saved);
      const combatEff = effectiveCombativeness(saved);
      const effs = statsHost.querySelectorAll('.account-profile-stat__eff');
      if (effs[0]) effs[0].textContent = `Bonus virage +${cornerEff}`;
      if (effs[1]) effs[1].textContent = `Bonus combat +${combatEff}`;
      opts.onDriverSaved(saved);
    } catch {
      paintStats();
    } finally {
      statsHost.classList.remove('account-profile-stats--saving');
    }
  };

  setStaffAssetBadgeFile(imageBadge, driver.imagePath);
  mountEntityImageBlock(imageWrap, {
    entityId: driverId,
    collection: 'drivers',
    imagePath: driver.imagePath,
    alt: 'Avatar du pilote',
    pickerTitle: 'Choisir un avatar',
    handlers: {
      uploadFromPc: (file) => uploadDriverImage(driverId, file),
      assignFromLibrary: (publicPath) => assignDriverImageFromLibrary(driverId, publicPath),
    },
    onAfterSave: (saved) => {
      const path =
        saved && typeof saved === 'object' && 'imagePath' in saved
          ? (saved as { imagePath?: string | null }).imagePath
          : null;
      setStaffAssetBadgeFile(imageBadge, path);
      if (saved && typeof saved === 'object' && 'id' in saved) {
        current = saved as Driver;
        opts.onDriverSaved(current);
      }
      applyRoleVisibility();
    },
  });
  const pilotImageMain = imageWrap.querySelector<HTMLElement>('.car-detail-image-main');
  if (pilotImageMain) pinStaffBadgeOnMedia(pilotImageMain, imageBadge);

  paintIdentity();
  paintStats();
  applyRoleVisibility();

  const season = await fetchActiveGlobalSeason().catch(() => null);
  const division = current.division;
  const seasonId =
    season != null && (division === 1 || division === 2 || division === 3 || division === 4)
      ? (season.divisions[division]?.season.id ?? null)
      : null;

  let profile: DriverSeasonProfile | null = null;
  if (seasonId != null) {
    profile = await fetchDriverSeasonProfile(seasonId, driverId).catch(() => null);
  }
  opts.onProfileReady(current, profile);
}

async function fillCarColumn(
  col: HTMLElement,
  carId: number,
  imageBadge: HTMLElement,
  modelBadge: HTMLElement
): Promise<void> {
  const header = document.createElement('div');
  header.className = 'account-profile-header';
  const identity = document.createElement('div');
  identity.className = 'account-profile-identity';
  const statsHost = document.createElement('div');
  statsHost.className = 'account-profile-stats';
  header.append(identity, statsHost);
  col.appendChild(header);

  const imageWrap = document.createElement('div');
  col.appendChild(imageWrap);

  const modelBlock = document.createElement('div');
  modelBlock.className = 'account-car-model3d';
  const modelFrame = document.createElement('div');
  modelFrame.className = 'account-car-model3d__frame account-profile-media-frame';
  const modelPreview = document.createElement('div');
  modelPreview.className = 'car-detail-3d account-car-model3d__preview';
  modelPreview.setAttribute('aria-label', 'Aperçu du modèle 3D');
  modelFrame.appendChild(modelPreview);
  const modelActions = document.createElement('div');
  modelActions.className = 'account-car-model3d__actions';
  modelActions.dataset.role = 'admin,moderator';
  const model3dBtn = document.createElement('button');
  model3dBtn.type = 'button';
  model3dBtn.className = 'btn btn-secondary btn-sm';
  model3dBtn.textContent = 'Choisir un modèle 3D';
  modelActions.appendChild(model3dBtn);
  modelBlock.append(modelFrame, modelActions);
  col.appendChild(modelBlock);

  let preview3d: Car3dPreviewHandle | null = null;
  let currentCar: Car | null = null;

  const refreshModelPreview = (car: Car): void => {
    preview3d?.dispose();
    preview3d = null;
    modelPreview.innerHTML = '';
    setStaffAssetBadgeFile(modelBadge, car.model3dPath);
    pinStaffBadgeOnMedia(modelFrame, modelBadge);
    preview3d = mountCar3dPreview(modelPreview, cacheBustCarGlbUrl(resolveCar3dModelUrl(car)));
    applyRoleVisibility();
  };

  const paintCarStats = (): void => {
    if (!currentCar) return;
    const lineEff = effectiveStraightLine(currentCar);
    const startEff = effectiveStart(currentCar);
    const editable = canEditDriverStats(getCurrentUser()?.role);
    statsHost.innerHTML = `
      <div class="account-profile-stat" data-stat="engine" data-current-value="${currentCar.engine}">
        <span class="account-profile-stat__label">Moteur</span>
        <span class="star-display"></span>
        <span class="account-profile-stat__eff">Bonus ligne +${lineEff}</span>
      </div>
      <div class="account-profile-stat" data-stat="tire" data-current-value="${currentCar.tire}">
        <span class="account-profile-stat__label">Pneus</span>
        <span class="star-display"></span>
        <span class="account-profile-stat__eff">Bonus départ +${startEff}</span>
      </div>
      <div class="account-profile-stat account-profile-stat--total">
        <span class="account-profile-stat__label">Total</span>
        <strong data-car-power>${carPower(currentCar)}★</strong>
      </div>
    `;
    const rows = statsHost.querySelectorAll<HTMLElement>('.account-profile-stat[data-stat]');
    rows.forEach((row) => {
      const display = row.querySelector('.star-display') as HTMLElement;
      const key = row.dataset.stat as 'engine' | 'tire';
      const initial = clampCarStat(Number(row.dataset.currentValue ?? currentCar![key]));
      if (!display) return;
      if (!editable) {
        display.innerHTML = renderStarsCar(initial);
        return;
      }
      mountInteractiveStarsCar(display, initial, (value) => {
        row.dataset.currentValue = String(value);
        void saveCarStats();
      });
    });
  };

  const saveCarStats = async (): Promise<void> => {
    if (!currentCar?.id || !canEditDriverStats(getCurrentUser()?.role)) return;
    const engineRow = statsHost.querySelector<HTMLElement>('[data-stat="engine"]');
    const tireRow = statsHost.querySelector<HTMLElement>('[data-stat="tire"]');
    const engine = clampCarStat(Number(engineRow?.dataset.currentValue ?? currentCar.engine));
    const tire = clampCarStat(Number(tireRow?.dataset.currentValue ?? currentCar.tire));
    statsHost.classList.add('account-profile-stats--saving');
    try {
      const saved = await updateCar(currentCar.id, {
        name: currentCar.name,
        engine,
        tire,
        division: currentCar.division,
        imagePath: currentCar.imagePath ?? null,
        model3dPath: currentCar.model3dPath ?? null,
      });
      currentCar = saved;
      const power = statsHost.querySelector('[data-car-power]');
      if (power) power.textContent = `${carPower(saved)}★`;
      const lineEff = effectiveStraightLine(saved);
      const startEff = effectiveStart(saved);
      const effs = statsHost.querySelectorAll('.account-profile-stat__eff');
      if (effs[0]) effs[0].textContent = `Bonus ligne +${lineEff}`;
      if (effs[1]) effs[1].textContent = `Bonus départ +${startEff}`;
    } catch {
      paintCarStats();
    } finally {
      statsHost.classList.remove('account-profile-stats--saving');
    }
  };

  let car: Car;
  try {
    car = await fetchCarById(carId);
  } catch {
    identity.innerHTML = '<p class="muted">Impossible de charger la voiture.</p>';
    return;
  }
  currentCar = car;
  setStaffAssetBadgeFile(imageBadge, car.imagePath);
  setStaffAssetBadgeFile(modelBadge, car.model3dPath);

  mountEntityImageBlock(imageWrap, {
    entityId: carId,
    collection: 'cars',
    imagePath: car.imagePath,
    alt: 'Visuel de la voiture',
    pickerTitle: 'Choisir un visuel',
    handlers: {
      uploadFromPc: (file) => uploadCarImage(carId, file),
      assignFromLibrary: (publicPath) => assignCarImageFromLibrary(carId, publicPath),
    },
    onAfterSave: (saved) => {
      const path =
        saved && typeof saved === 'object' && 'imagePath' in saved
          ? (saved as { imagePath?: string | null }).imagePath
          : null;
      setStaffAssetBadgeFile(imageBadge, path);
      if (saved && typeof saved === 'object' && 'id' in saved) currentCar = saved as Car;
      applyRoleVisibility();
    },
  });
  const carImageMain = imageWrap.querySelector<HTMLElement>('.car-detail-image-main');
  if (carImageMain) pinStaffBadgeOnMedia(carImageMain, imageBadge);

  identity.innerHTML = `
    <p class="account-profile-name">${escapeHtml(car.name)}</p>
    <p class="account-profile-meta">Division ${escapeHtml(divisionLabel(car.division))}</p>
  `;

  paintCarStats();
  refreshModelPreview(car);

  model3dBtn.addEventListener('click', () => {
    openCar3dModelPicker({
      title: 'Choisir un modèle 3D',
      currentPath: currentCar?.model3dPath ?? null,
      onSelect: (item) => {
        void assignCarModel3dPath(carId, item ? item.publicPath : null).then((saved) => {
          currentCar = saved;
          refreshModelPreview(saved);
        });
      },
      onUploadFromPc: (file) =>
        uploadCarModel3d(carId, file).then((saved) => {
          currentCar = saved;
          refreshModelPreview(saved);
          return undefined;
        }),
    });
  });

  applyRoleVisibility();
}
